import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { api, ApiError } from "@/lib/api";
import type { AdminOrgMeta } from "@shared/protocol";
import { relativeTime } from "@/lib/utils";

function autoSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32);
}

/** Super-admin-only: every org on the platform, not just the ones the
 * viewer belongs to. Gated server-side by /api/admin/orgs (super_admin
 * role required) — the nav entry that links here is hidden for everyone
 * else, but the route itself relies on the backend check, not on that. */
export function OrganizationsPage() {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState<AdminOrgMeta[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [creating, setCreating] = useState(false);

  function load() {
    api<{ ok: true; orgs: AdminOrgMeta[] }>("/api/admin/orgs")
      .then((res) => setOrgs(res.orgs))
      .catch((err) =>
        setError(
          err instanceof ApiError
            ? err.message
            : "failed to load organizations",
        ),
      );
  }

  useEffect(load, []);

  function handleNameChange(value: string) {
    setName(value);
    if (!slugEdited) setSlug(autoSlug(value));
  }

  async function handleCreate() {
    setCreating(true);
    try {
      await api("/api/admin/orgs", {
        method: "POST",
        body: JSON.stringify({ name: name.trim(), slug: slug.trim() }),
      });
      setShowCreate(false);
      setName("");
      setSlug("");
      setSlugEdited(false);
      load();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "failed to create organization",
      );
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="max-w-5xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Organizations</h1>
          <p className="text-sm text-muted-foreground">
            Every organization on the platform.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)}>New organization</Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            All organizations{orgs ? ` (${orgs.length})` : ""}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Members</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orgs?.map((o) => (
                <TableRow
                  key={o.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/admin/orgs/${o.id}`)}
                >
                  <TableCell className="font-medium underline-offset-2 hover:underline">
                    {o.name}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {o.slug}
                  </TableCell>
                  <TableCell>{o.member_count}</TableCell>
                  <TableCell>
                    {o.is_super ? (
                      <Badge>Super</Badge>
                    ) : (
                      <Badge variant="outline">Regular</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {relativeTime(o.created_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create organization</DialogTitle>
            <DialogDescription>
              Starts with no members — add users to it from the org's detail
              page.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 my-4">
            <div className="space-y-1.5">
              <Label htmlFor="org-name">Name</Label>
              <Input
                id="org-name"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Acme Inc"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="org-slug">Slug</Label>
              <Input
                id="org-slug"
                value={slug}
                onChange={(e) => {
                  setSlug(e.target.value);
                  setSlugEdited(true);
                }}
                placeholder="acme-inc"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!name.trim() || !slug.trim() || creating}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
